/**
 * Carrousel accessible — Clarisse Ka
 *
 * S'appuie sur le scroll-snap natif (la piste reste défilable sans JS) et y
 * ajoute boutons précédent/suivant, puces de pagination et lecture auto.
 *
 * Markup attendu :
 *   [data-carousel] (racine ; option : data-carousel-autoplay="7000" en ms)
 *     [data-carousel-track]   piste flex + scroll-snap-x → slides (enfants directs)
 *     [data-carousel-prev]    bouton précédent
 *     [data-carousel-next]    bouton suivant
 *     [data-carousel-dots]    conteneur des puces (générées ici)
 *
 * Dégradation : sans JS la piste reste scrollable ; les contrôles sont du
 * markup inerte. Respecte prefers-reduced-motion (pas d'autoplay, pas de
 * défilement animé).
 */

const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

export function initCarousels() {
  document.querySelectorAll("[data-carousel]").forEach(setupCarousel);
}

function setupCarousel(root) {
  const track = root.querySelector("[data-carousel-track]");
  if (!track) return;

  const slides = Array.from(track.children);
  const prevBtn = root.querySelector("[data-carousel-prev]");
  const nextBtn = root.querySelector("[data-carousel-next]");
  const dotsWrap = root.querySelector("[data-carousel-dots]");

  // Largeur d'un pas = largeur d'une slide + gap inter-slides.
  const step = () => {
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return slides[0].getBoundingClientRect().width + gap;
  };

  const isOverflowing = () => track.scrollWidth - track.clientWidth > 4;

  // Tout tient dans la vue → contrôles inutiles : on masque et on centre.
  if (slides.length <= 1 || !isOverflowing()) {
    [prevBtn, nextBtn, dotsWrap].forEach(
      (el) => el && el.classList.add("hidden"),
    );
    track.classList.add("justify-center");
    track.classList.remove("cursor-grab"); // rien à faire glisser
    return;
  }

  const activeIndex = () => Math.round(track.scrollLeft / step());

  const scrollToIndex = (i) => {
    const idx = Math.max(0, Math.min(i, slides.length - 1));
    track.scrollTo({
      left: idx * step(),
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };

  // ── Puces de pagination (une par slide) ──────────────────────────────────
  const dots = [];
  if (dotsWrap) {
    slides.forEach((_, i) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className =
        "h-2 w-2 rounded-full bg-neutral/30 transition-all duration-300 hover:bg-primary/60";
      dot.setAttribute("aria-label", `Aller au témoignage ${i + 1}`);
      dot.addEventListener("click", () => scrollToIndex(i));
      dotsWrap.appendChild(dot);
      dots.push(dot);
    });
  }

  const atEnd = () =>
    track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;

  const updateUi = () => {
    const i = activeIndex();
    dots.forEach((dot, di) => {
      const on = di === i;
      dot.classList.toggle("bg-primary", on);
      dot.classList.toggle("w-6", on);
      dot.classList.toggle("bg-neutral/30", !on);
      dot.classList.toggle("w-2", !on);
    });
    if (prevBtn) {
      prevBtn.classList.toggle("opacity-30", i <= 0);
      prevBtn.classList.toggle("pointer-events-none", i <= 0);
    }
    if (nextBtn) {
      nextBtn.classList.toggle("opacity-30", atEnd());
      nextBtn.classList.toggle("pointer-events-none", atEnd());
    }
  };

  prevBtn?.addEventListener("click", () => scrollToIndex(activeIndex() - 1));
  nextBtn?.addEventListener("click", () => scrollToIndex(activeIndex() + 1));

  // ── Glisser à la souris (le tactile utilise le scroll natif) ──────────────
  let isDown = false;
  let startX = 0;
  let startScroll = 0;
  let moved = false;

  track.addEventListener("pointerdown", (e) => {
    if (e.pointerType === "touch") return; // tactile = défilement natif
    isDown = true;
    moved = false;
    startX = e.clientX;
    startScroll = track.scrollLeft;
    // Désactiver le défilement « smooth » pendant le glisser (sinon saccadé).
    track.style.scrollBehavior = "auto";
    track.classList.add("cursor-grabbing");
    track.setPointerCapture(e.pointerId);
  });

  track.addEventListener("pointermove", (e) => {
    if (!isDown) return;
    const dx = e.clientX - startX;
    if (Math.abs(dx) > 4) moved = true;
    track.scrollLeft = startScroll - dx;
  });

  const endDrag = (e) => {
    if (!isDown) return;
    isDown = false;
    track.classList.remove("cursor-grabbing");
    track.style.scrollBehavior = "";
    try {
      track.releasePointerCapture(e.pointerId);
    } catch (_) {
      /* pointer déjà relâché */
    }
    // Recaler sur la slide la plus proche après le glisser.
    scrollToIndex(activeIndex());
  };

  track.addEventListener("pointerup", endDrag);
  track.addEventListener("pointercancel", endDrag);

  // Annuler le clic « fantôme » déclenché à la fin d'un glisser.
  track.addEventListener(
    "click",
    (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );

  let raf;
  track.addEventListener(
    "scroll",
    () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateUi);
    },
    { passive: true },
  );
  window.addEventListener("resize", () => requestAnimationFrame(updateUi), {
    passive: true,
  });

  updateUi();

  // ── Lecture automatique (optionnelle) ────────────────────────────────────
  const delay = parseInt(root.dataset.carouselAutoplay || "", 10);
  if (delay > 0 && !prefersReducedMotion) {
    let timer;
    const tick = () => scrollToIndex(atEnd() ? 0 : activeIndex() + 1);
    const start = () => {
      stop();
      timer = window.setInterval(tick, delay);
    };
    const stop = () => window.clearInterval(timer);

    start();
    ["pointerenter", "focusin"].forEach((e) => root.addEventListener(e, stop));
    ["pointerleave", "focusout"].forEach((e) =>
      root.addEventListener(e, start),
    );
    document.addEventListener("visibilitychange", () =>
      document.hidden ? stop() : start(),
    );
  }
}
